import type { ComplianceOverviewData } from '../../../services/LicenseQueryService';
import {
	FEDERAL_EXCLUSION_LIST_IDS,
	federalExclusionListPath,
} from '../license-exclusion';
import {
	COMPLIANCE_ATTENTION_LIST_META,
	COMPLIANCE_RECORD_LIST_META,
} from './client-status';
import {
	COMPLIANCE_ATTENTION_LIST_IDS,
	type ComplianceAttentionListId,
} from './list-ids';

/**
 * PD-16306 — the Compliance dashboard's Needs Attention cards, defined once:
 * what each is called, where its Review link goes, and which figure of the
 * dashboard payload it shows.
 *
 * WHY. Two surfaces tell a customer about these cards: the dashboard itself
 * and the weekly compliance summary email. The daily digest the email
 * replaces kept its own queries and its own links, and every one of its
 * figures disagreed with the dashboard (it would have told Pharmsource
 * 66,186 matches were waiting for review). Reading the title, the link and
 * the count from one table means the email can only ever repeat what the
 * dashboard says.
 */

/** The dashboard page itself. */
export const COMPLIANCE_DASHBOARD_PATH = '/compliance/dashboard';

/**
 * The Found License Fills a Gap list. It lives on the Matching page, not
 * among the Needs Attention lists, because a found license is a suggestion
 * rather than a license on file (PD-16256 / PD-16467).
 */
export const LICENSE_MATCH_FILLS_GAP_LIST_ID = 'license-match-fills-gap';

/**
 * A Needs Attention list's URL, optionally narrowed by one filter
 * (`<filterKey>:<value>`). No Customer filter: every attention list locks the
 * active-buyer filter, which already implies it.
 */
export function complianceAttentionListPath(
	listId: ComplianceAttentionListId,
	filter?: string,
): string {
	const base = `/compliance/attention?list=${listId}`;
	return filter != null ? `${base}&f=${encodeURIComponent(filter)}` : base;
}

/**
 * The dashboard's card groups, in order. PD-16652 added the third, Check Your
 * Records: records for the customer to tidy (Duplicate License, Shared
 * License), after the two groups about accounts at risk.
 */
export type ComplianceAttentionCardGroup =
	| 'fixNow'
	| 'improve'
	| 'checkRecords';

export const COMPLIANCE_ATTENTION_CARD_GROUPS: ReadonlyArray<{
	group: ComplianceAttentionCardGroup;
	label: string;
	/**
	 * A line under the heading, where the group differs from the others:
	 * Check Your Records covers every customer account (not only active
	 * buyers) and counts license numbers.
	 */
	note?: string;
}> = [
	{ group: 'fixNow', label: 'Fix Now' },
	{ group: 'improve', label: 'One Click to Improve' },
	{
		group: 'checkRecords',
		label: 'Check Your Records',
		note: 'License records to tidy on any customer account, counted in license numbers.',
	},
];

/**
 * What a card's figure counts. Every Needs Attention card counts ACCOUNTS;
 * the Check Your Records cards count LICENSE NUMBERS — one per group to
 * review ("973 license numbers"), however many accounts or copies it holds
 * (Chris, 2026-09-28).
 */
export type ComplianceAttentionCardUnit = 'account' | 'licenseNumber';

/** "1 account" / "973 license numbers" — the unit worded for a figure. */
export function complianceAttentionCardUnitLabel(
	unit: ComplianceAttentionCardUnit,
	count: number,
): string {
	if (unit === 'licenseNumber') {
		return count === 1 ? 'license number' : 'license numbers';
	}
	return count === 1 ? 'account' : 'accounts';
}

/**
 * A card is one of the attention lists, Found License Fills a Gap, or Federal
 * Exclusion (PD-13954), whose list is its own page because its rows are OIG
 * matches, not licenses.
 */
export type ComplianceAttentionCardId =
	| ComplianceAttentionListId
	| 'fillsGap'
	| 'federalExclusion';

/** The part of the dashboard payload the cards read. */
export type ComplianceAttentionFiguresModel =
	ComplianceOverviewData['attention'];

export interface ComplianceAttentionCardModel {
	id: ComplianceAttentionCardId;
	/** The card's title on the dashboard. */
	title: string;
	group: ComplianceAttentionCardGroup;
	/** Where the card's Review link goes, as an app path. */
	href: string;
	/** What `count` counts. */
	unit: ComplianceAttentionCardUnit;
	/**
	 * The card's figure in `unit`: ACCOUNTS (active buyers) on every Needs
	 * Attention card, LICENSE NUMBERS (groups) on the Check Your Records
	 * cards. Null when the server could not check (Number Not Found without
	 * the lookups; a Check Your Records card when the groups could not be
	 * read); the card then says so, because a zero would claim there is
	 * nothing to look at. A figure an older cached payload omits reads 0, as
	 * it always has on the dashboard.
	 */
	count: (attention: ComplianceAttentionFiguresModel) => number | null;
}

/** Which dashboard figure each list-backed card shows. */
const LIST_CARD_COUNTS: Record<
	Exclude<
		ComplianceAttentionListId,
		// No card of their own: Recently Fixed is not a problem, and the two
		// Adverse Action views are figures on the Adverse Action card
		// (PD-16615). The Check Your Records cards read RECORD_CARD_COUNTS.
		| 'compliance-recently-fixed'
		| 'compliance-adverse-flag-only'
		| 'compliance-adverse-reviewed'
		| 'compliance-duplicate-license'
		| 'compliance-shared-license'
		// PD-16653: figures on their cards (recordGroups.*.reviewed).
		| 'compliance-duplicate-license-reviewed'
		| 'compliance-shared-license-reviewed'
	>,
	ComplianceAttentionCardModel['count']
> = {
	[COMPLIANCE_ATTENTION_LIST_IDS.controlledNoDea]: (a) =>
		a.controlledNoDea ?? 0,
	[COMPLIANCE_ATTENTION_LIST_IDS.deadLicense]: (a) => a.deadLicense,
	[COMPLIANCE_ATTENTION_LIST_IDS.adverseAction]: (a) => a.adverseAction ?? 0,
	[COMPLIANCE_ATTENTION_LIST_IDS.wrongNumber]: (a) => a.wrongNumber ?? 0,
	[COMPLIANCE_ATTENTION_LIST_IDS.failedLookup]: (a) => a.failedLookup ?? null,
	[COMPLIANCE_ATTENTION_LIST_IDS.expiringSoon]: (a) => a.expiringSoon,
	[COMPLIANCE_ATTENTION_LIST_IDS.noVerifiedLicense]: (a) =>
		a.noActiveLicense.total,
	[COMPLIANCE_ATTENTION_LIST_IDS.linkEnded]: (a) => a.linkEnded,
	[COMPLIANCE_ATTENTION_LIST_IDS.readyToConfirm]: (a) => a.unconfirmedMatch,
	[COMPLIANCE_ATTENTION_LIST_IDS.replacementAvailable]: (a) =>
		a.replacementAvailable ?? 0,
};

/**
 * PD-16652 — the Check Your Records figures: GROUPS to review, net of the
 * groups a review is hiding. Null when the server could not read the groups;
 * absent (an older payload) reads 0.
 */
const RECORD_CARD_COUNTS: Record<
	'compliance-duplicate-license' | 'compliance-shared-license',
	ComplianceAttentionCardModel['count']
> = {
	[COMPLIANCE_ATTENTION_LIST_IDS.duplicateLicense]: (a) =>
		a.duplicateLicense === null ? null : (a.duplicateLicense ?? 0),
	[COMPLIANCE_ATTENTION_LIST_IDS.sharedLicense]: (a) =>
		a.sharedLicense === null ? null : (a.sharedLicense ?? 0),
};

/**
 * PD-13954 — the Federal Exclusion card: active buyers with an open strong
 * match to the OIG exclusion list (the same NPI, or the same name at the same
 * address). A Fix Now card beside Adverse Action, reviewed and marked off the
 * same way (Chris, 2026-10-07). Its list is its own page
 * (`/compliance/exclusions`): a row is an OIG match on an account or a linked
 * person, not a license. Not part of the license coverage figures.
 */
const FEDERAL_EXCLUSION_CARD: ComplianceAttentionCardModel = {
	id: 'federalExclusion',
	title: 'Federal Exclusion',
	group: 'fixNow',
	href: federalExclusionListPath(FEDERAL_EXCLUSION_LIST_IDS.strong),
	unit: 'account',
	// Null when the server could not read the matches (the card says it
	// could not check); an older payload without the figure reads 0.
	count: (a) =>
		a.federalExclusion === null ? null : (a.federalExclusion ?? 0),
};

/** The One Click cards; every other list-backed card is Fix Now. */
const IMPROVE_LISTS: ReadonlySet<string> = new Set([
	COMPLIANCE_ATTENTION_LIST_IDS.readyToConfirm,
	COMPLIANCE_ATTENTION_LIST_IDS.replacementAvailable,
]);

/**
 * Every Needs Attention card in dashboard order: the list-backed cards in
 * COMPLIANCE_ATTENTION_LIST_META order (Fix Now, then One Click) with Federal
 * Exclusion right after Adverse Action (PD-13954), then Found License Fills a
 * Gap, then the Check Your Records cards (COMPLIANCE_RECORD_LIST_META,
 * PD-16652). Titles come from the list meta, so a card, the client badge and
 * the email name each problem the same way.
 */
export const COMPLIANCE_ATTENTION_CARDS: ReadonlyArray<ComplianceAttentionCardModel> =
	[
		...COMPLIANCE_ATTENTION_LIST_META.flatMap(
			(meta): Array<ComplianceAttentionCardModel> => {
				// Recently Fixed has no card; a list added to the meta without
				// a figure here fails loudly at load rather than showing 0.
				const count: ComplianceAttentionCardModel['count'] | undefined = (
					LIST_CARD_COUNTS as Partial<
						Record<string, ComplianceAttentionCardModel['count']>
					>
				)[meta.listId];
				if (count == null) {
					throw new Error(`No dashboard figure for ${meta.listId}`);
				}
				const card: ComplianceAttentionCardModel = {
					id: meta.listId,
					title: meta.title,
					group: IMPROVE_LISTS.has(meta.listId) ? 'improve' : 'fixNow',
					href: complianceAttentionListPath(meta.listId),
					unit: 'account',
					count,
				};
				return meta.listId === COMPLIANCE_ATTENTION_LIST_IDS.adverseAction
					? [card, FEDERAL_EXCLUSION_CARD]
					: [card];
			},
		),
		{
			id: 'fillsGap',
			title: 'Found License Fills a Gap',
			group: 'improve',
			href: `/licenses/matching?list=${LICENSE_MATCH_FILLS_GAP_LIST_ID}`,
			unit: 'account',
			count: (a) => a.fillsGap ?? 0,
		},
		...COMPLIANCE_RECORD_LIST_META.map((meta): ComplianceAttentionCardModel => {
			const count: ComplianceAttentionCardModel['count'] | undefined = (
				RECORD_CARD_COUNTS as Partial<
					Record<string, ComplianceAttentionCardModel['count']>
				>
			)[meta.listId];
			if (count == null) {
				throw new Error(`No dashboard figure for ${meta.listId}`);
			}
			return {
				id: meta.listId,
				title: meta.title,
				group: 'checkRecords',
				href: complianceAttentionListPath(meta.listId),
				unit: 'licenseNumber',
				count,
			};
		}),
	];

/** One card by id. Throws for an id that is not a card. */
export function complianceAttentionCard(
	id: ComplianceAttentionCardId,
): ComplianceAttentionCardModel {
	const card = COMPLIANCE_ATTENTION_CARDS.find((entry) => entry.id === id);
	if (card == null) {
		throw new Error(`No compliance attention card ${id}`);
	}
	return card;
}
